import { useTranslation } from "react-i18next";

import { Schema, useSchemaNames } from "services/databases/useSchemaNames";
import { SYSTEM_SCHEMA_NAMES } from "services/databases/utils";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import { GrantPrivilegesSelector } from "../PrivilegesTable/GrantPrivilegesSelector";
import { GroupInner } from "../PrivilegesTable/GroupInner";
import { ALL_SCHEMAS } from "../PrivilegesTable/constant";
import { usePrivilegesHandlers } from "../PrivilegesTable/usePrivilegesHandlers";
import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { schemaPrivileges } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";

import groupStyles from "../PrivilegesTable/styles.module.scss";
import styles from "./styles.module.scss";

type Props = {
  index: number;
  resource: string;
  catalogName: string;
};

export const SchemasGroup = (props: Props) => {
  const { index, resource, catalogName } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();

  const isViewOnly = useViewOnly();

  const schemas = useSchemaNames({ database: catalogName }).filter(
    schema => !SYSTEM_SCHEMA_NAMES.includes(schema.schemaName)
  );

  const schemaState = privilegesState.databasePrivileges?.[catalogName]
    ?.schema ?? [{ toAssign: [], toDeny: [], objects: [] }];

  const groupState = schemaState[index];
  const { objects, expanded } = groupState;
  const menu = useMenu();

  const { onGrantPrivileges, deleteGroup, handleMore } = usePrivilegesHandlers({
    index,
    resource,
    catalogName,
  });

  const { t } = useTranslation();

  const menuItems = [
    [
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [{ name: ALL_SCHEMAS, catalogName }],
            catalogName,
          });
        },
        testId: "roles-wizard-add-schemas-group",
        disabled: isViewOnly,
      },
    ],
    ...(schemaState.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-schema-group",
              disabled: isViewOnly,
            },
          ],
        ]
      : []),
  ];

  const selectedSchemas = objects.map(object => object.name);

  const handleSelect = (items: string[]) => {
    const objects = items.map(item => {
      return {
        name: item,
        catalogName,
      };
    });
    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      objects,
      index,
      catalogName,
    });
  };

  const prefixItems = [
    <ContextMenuItem
      disabled={isViewOnly}
      value={ALL_SCHEMAS}
      key={ALL_SCHEMAS}
      checked={selectedSchemas.includes(ALL_SCHEMAS)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_schemas")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() => {
        handleSelect([ALL_SCHEMAS]);
      }}
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const filterBulkResources = (item: { name: string; catalogName: string }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    if (item.catalogName === catalogName) {
      return item.name !== ALL_SCHEMAS;
    }
  };

  const handleResource = (schema: { schemaName: string }) => {
    const selectedObjects = selectedSchemas.includes(schema.schemaName)
      ? objects.filter(object => {
          if (object.catalogName !== catalogName) {
            return true;
          }
          return object.name !== schema.schemaName;
        })
      : [...objects, { name: schema.schemaName, catalogName }].filter(
          filterBulkResources
        );

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      index,
      catalogName,
      objects: selectedObjects,
    });
  };

  const renderItem = (schema: Schema) => {
    return (
      <ContextMenuItem
        disabled={isViewOnly}
        key={schema.schemaName}
        value={schema.schemaName}
        text={schema.schemaName}
        direction="left"
        checked={selectedSchemas.includes(schema.schemaName)}
        onClick={() => {
          handleResource(schema);
        }}
        testId={`schema-${schema.schemaName}`}
      />
    );
  };

  const privilegesOptions = schemaPrivileges.map(privilege => {
    return {
      value: privilege,
      text: formatPrivilege(privilege),
    };
  });

  const renderCatalogItem = (name: string) => {
    return <span className={styles.resourceName}>{name}</span>;
  };

  return (
    <div className={groupStyles.group}>
      <GroupInner
        handleMore={handleMore}
        expanded={expanded}
        objects={objects}
        prefixItems={prefixItems}
        renderCatalogItem={renderCatalogItem}
        items={schemas}
        renderItem={renderItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_schema")}
        searchKey="schemaName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
